import { defineComponent as _defineComponent } from 'vue'
type RowDetails = { el: HTMLElement | null; rowRef: string | undefined }

import OzDivider, { OzDividerColorScheme } from '@@/library/v4/components/OzDivider.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import type { ComponentPublicInstance } from 'vue'
import { uniqueId } from 'lodash-es'
import { computed, onMounted, ref, useSlots, watch } from 'vue'

export enum OzMenuRowsSizePreset {
  H44px = 'H44px',
  H48px = 'H48px',
  H56px = 'H56px',
}

export enum OzMenuRowsFocusStyle {
  None = 'None',
  Bubble100 = 'Bubble100',
  Bubble200 = 'Bubble200',
  Grey100 = 'Grey100',
  HighlightText = 'HighlightText',
  ButtonDefault = 'ButtonDefault',
}

export enum OzMenuRowsRowStyle {
  Default = 'Default',
  Primary = 'Primary',
  Secondary = 'Secondary',
  Danger = 'Danger',
  Disabled = 'Disabled',
  Highlighted = 'Highlighted',
  Focused = 'Focused',
}

export enum OzMenuRowsHrefMode {
  Default = 'Default',
  DisplayOnly = 'DisplayOnly',
  HistoryPushState = 'HistoryPushState',
}

export interface OzMenuRowsRow<EventPayload = any> {
  // event payload that is emitted when the row is selected
  eventPayload?: EventPayload

  // navigation state
  href?: string
  hrefMode?: OzMenuRowsHrefMode
  isDisabled?: boolean

  // this is used to identifying active row that is given from the props
  ref?: string

  // misc
  testId?: string
  analyticsId?: string

  // text customization
  text?: string
  htmlText?: string

  // text styling
  textStyleClass?: string
  textTruncated?: boolean

  // left icon customization
  icon?: string
  iconSize?: number
  leftIconClasses?: string

  // right icon customization
  rightIcon?: string | null
  rightIconSize?: number
  rightLabel?: string
  rightIconAriaLabel?: string
  rightIconHighlighted?: boolean

  // row styling
  rowStyle?: OzMenuRowsRowStyle
  rowClasses?: string

  // Accessibility
  ariaChecked?: string
  ariaLabel?: string
  /**
   * The description will be inserted in an SR-only container
   * and will be connected to the row through aria-describedby.
   */
  ariaDescription?: string
  role?: 'menuitem' | 'menuitemradio' | 'menuitemcheckbox'
}

const __default__ = {}

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'OzMenuRows',
  props: {
    rows: null,
    focusStyle: null,
    darkMode: { type: [Boolean, String], default: 'auto' },
    sizePreset: { default: OzMenuRowsSizePreset.H48px },
    xLastRowDivider: { type: Boolean, default: false },
    commonSettings: { default: () => [] as OzMenuRowsRow },
    isPhone: { type: Boolean, default: false },
    activeRow: { default: '' },
    accessibilityRole: { default: undefined }
  },
  emits: ["select-row", "mouseenter-row", "mouseleave-row", "focus-row", "blur-row"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'select-row', event: Event, payload: any): void
  (e: 'mouseenter-row', event: Event, payload: any): void
  (e: 'mouseleave-row', event: Event, payload: any): void
  (e: 'focus-row', event: Event, payload: any): void
  (e: 'blur-row', event: Event, payload: any): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { rows: OzMenuRowsRow[], focusStyle: OzMenuRowsFocusStyle, darkMode: boolean | 'auto', sizePreset: OzMenuRowsSizePreset, xLastRowDivider: boolean, commonSettings: OzMenuRowsRow, isPhone: boolean, activeRow: string, accessibilityRole: 'menu' | 'group' }





const slots = useSlots()

const rowEls = ref<Array<RowDetails>>([])
const setRowElRef = (index: number, el: Element | ComponentPublicInstance | null) => {
  if (rowEls.value[index] && el) rowEls.value[index].el = el as HTMLElement
}

watch(
  () => props.rows,
  (newRows) => {
    rowEls.value = newRows.map((row) => ({ el: null, rowRef: row.ref }))
  },
  { immediate: true, deep: true },
)

const iconSize = computed(() => {
  return {
    [OzMenuRowsSizePreset.H44px]: 16,
    [OzMenuRowsSizePreset.H48px]: 24,
    [OzMenuRowsSizePreset.H56px]: 24,
  }[props.sizePreset]
})

const rowsData = computed(() =>
  props.rows.map((row) => ({
    rowStyle: OzMenuRowsRowStyle.Default,
    ...props.commonSettings,
    ...row,
    ariaDescribedby: row.ariaDescription ? uniqueId('oz-menu-row-description-') : undefined,
  })),
)

const xLeftIcon = computed(() => rowsData.value.some((row) => row.icon))

const textHighlightStyles = computed(() => [
  {
    'hover-hover:group-hover:text-grape-500 hover-hover:dark:group-hover:text-canary-500': props.darkMode === 'auto',
    'hover-hover:group-hover:text-grape-500': props.darkMode === false,
    'hover-hover:group-hover:text-canary-500': props.darkMode === true,
  },
  {
    'group-focus-visible:text-grape-500 dark:group-focus-visible:text-canary-500': props.darkMode === 'auto',
    'group-focus-visible:text-grape-500': props.darkMode === false,
    'group-focus-visible:text-canary-500': props.darkMode === true,
  },
  {
    'group-active:text-grape-500 dark:group-active:text-canary-500': props.darkMode === 'auto',
    'group-active:text-grape-500': props.darkMode === false,
    'group-active:text-canary-500': props.darkMode === true,
  },
])

const getAccessibilityAttrs = (row: OzMenuRowsRow): Record<string, string | undefined> => {
  return {
    'aria-checked': row.ariaChecked,
    'aria-label': row.ariaLabel,
    'aria-describedby': row.ariaDescribedby,
    role: row.role || (props.accessibilityRole ? 'menuitem' : undefined),
  }
}

const selectRow = (event: Event, row: OzMenuRowsRow): void => {
  if (row.hrefMode !== OzMenuRowsHrefMode.Default) event.preventDefault()
  if (row.hrefMode === OzMenuRowsHrefMode.HistoryPushState) {
    // Store the url in the history state so you can read it later
    window.history.pushState({ url: row.href }, '', row.href)
  }

  emit('select-row', event, row.eventPayload)
}

const shouldShowRowFocusRing = (row: OzMenuRowsRow): boolean => {
  return !row.rightLabel
}

const scrollActiveRowIntoView = () => {
  if (!props.activeRow) return
  const activeRow = Object.values<RowDetails>(rowEls.value).find((obj) => obj.rowRef === props.activeRow)
  if (!activeRow) return
  activeRow?.el?.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' })
}

const focusRow = (textContent: string): void => {
  const elIndex = rowEls.value.findIndex((rowEl) => rowEl.el?.textContent?.includes(textContent))
  if (elIndex !== -1) {
    rowEls.value[elIndex].el?.firstChild?.focus()
  }
}

const SCROLL_DELAY = 500
onMounted(() => {
  if (props.isPhone) {
    // Prevent the UI from laggy on mobile when scrolling into view that contains the `activeRow`
    setTimeout(() => {
      scrollActiveRowIntoView()
    }, SCROLL_DELAY)
  } else {
    scrollActiveRowIntoView()
  }
})

expose({
  scrollActiveRowIntoView,
  focusRow,
})

return { __sfc: true,OzMenuRowsSizePreset, OzMenuRowsFocusStyle, OzMenuRowsRowStyle, OzMenuRowsHrefMode, props, emit, slots, rowEls, setRowElRef, iconSize, rowsData, xLeftIcon, textHighlightStyles, getAccessibilityAttrs, selectRow, shouldShowRowFocusRing, scrollActiveRowIntoView, focusRow, SCROLL_DELAY, OzDivider, OzDividerColorScheme, OzIcon }
}

})